import {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {setAppsecs} from '@/store/onboardingSlices'
import {AutoCompleteWrapper} from '@/components/GDS/molecules/AutoCompleteWrapper'
import {getRepoTitle} from '@/components/SelfOnboarding/util/getLabel'
import Button from '@/components/GDS/molecules/Button/Button'
import CardWrapper from '@/components/GDS/molecules/CardWrapper/Card'
import sharedStyles from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {APPSEC_WIDGET} from '@/components/SelfOnboarding/util/constants'
import {Feedback, Label} from 'pepsico-ds'
import {anyWidgetFieldTouched, hasEmptyRequiredFields} from '@/components/SelfOnboarding/util/widgetErrorUtils'
import type {RootState} from '@/store'

const initialAppSec = {
  url: '',
}

type Suggestion = {id: string; label: string; fullObject?: any}

const AppSecWidget = ({onValidationChange}: {onValidationChange?: (hasError: boolean) => void}) => {
  const appsecs = useSelector((state: any) => (Array.isArray(state.appsec) ? state.appsec : []))
  const dispatch = useDispatch()
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [error, setError] = useState('')
  const [touched, setTouched] = useState<{[idx: number]: {url?: boolean}}>({})
  const isDark = useSelector((state: RootState) => state.theme.mode === 'dark')

  const getNormalizedUrl = (value: any) => {
    const raw = typeof value === 'string' ? value : value && typeof value === 'object' ? value?.label : ''
    return typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  }

  const duplicateInfo = (() => {
    const counts = new Map<string, number>()
    const firstIndex = new Map<string, number>()

    appsecs.forEach((a: any, idx: number) => {
      const url = getNormalizedUrl(a?.url)
      if (!url) return

      counts.set(url, (counts.get(url) ?? 0) + 1)
      if (!firstIndex.has(url)) firstIndex.set(url, idx)
    })

    const duplicates = new Set<string>()
    counts.forEach((count, url) => {
      if (count > 1) duplicates.add(url)
    })

    return {counts, firstIndex, duplicates}
  })()

  const hasDuplicates = duplicateInfo.duplicates.size > 0

  useEffect(() => {
    if (!appsecs || appsecs.length === 0) {
      dispatch(setAppsecs([{...initialAppSec}]))
    }
  }, [appsecs, dispatch])

  const updateAppsec = (idx: number, value: string | {id: string; label: string}) => {
    const newAppsecs = appsecs.map((a: any, i: number) => (i === idx ? {...a, url: value} : a))
    dispatch(setAppsecs(newAppsecs))
  }
  const handleBlur = (idx: number, field: string) => {
    setTouched(prev => ({
      ...prev,
      [idx]: {
        ...(prev[idx] || {}),
        [field]: true,
      },
    }))
  }

  const addAppsec = () => {
    dispatch(setAppsecs([...appsecs, {...initialAppSec}]))
  }

  const removeAppsec = (idx: number) => {
    dispatch(setAppsecs(appsecs.filter((_: any, i: number) => i !== idx)))
    setTouched(prev => {
      const copy = {...prev}
      delete copy[idx]
      return copy
    })
  }

  const isValidUrl = (str: string) => {
    try {
      new URL(str)
      return true
    } catch {
      return false
    }
  }

  useEffect(() => {
    const widgetTouched = anyWidgetFieldTouched(touched)
    const hasError = (widgetTouched && hasEmptyRequiredFields(['url'], appsecs)) || hasDuplicates
    if (onValidationChange) onValidationChange(hasError)
  }, [appsecs, touched, onValidationChange, hasDuplicates])

  return (
    <>
      {appsecs.map((appsec: any, idx: number) => (
        <CardWrapper
          cardHeader={{
            title: `Repo ${idx + 1}`,
            rightAccessory:
              idx > 0
                ? {
                    iconButtons: [
                      {
                        icon: 'delete',
                        onClick: () => removeAppsec(idx),
                      },
                    ],
                  }
                : {
                    iconButtons: [
                      {
                        icon: 'delete_outline',
                        onClick: () => {
                          if (appsecs.length > 1) {
                            dispatch(setAppsecs(appsecs.map((a: any, i: number) => (i === 0 ? {...a, url: ''} : a))))
                            setTouched(prev => {
                              const copy = {...prev}
                              delete copy[0]
                              return copy
                            })
                          } else {
                            dispatch(setAppsecs([{url: ''}]))
                            setTouched({})
                          }
                        },
                      },
                    ],
                  },
          }}
          cardBody={{
            type: 'slot',
          }}
          state="non-interactive"
          type="basic"
          key={idx}
        >
          <form className="flex w-full flex-col items-start">
            <div className="flex w-[282px] flex-col items-start">
              <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                <Label className={sharedStyles.autocompleteLabelFont} label="Repo URL" />
                <AutoCompleteWrapper
                  icon="keyboard_arrow_down"
                  limitSuggestions={30}
                  placeholderText="Search AppSec Repo URL"
                  onClear={() => updateAppsec(idx, '')}
                  suggestions={suggestions}
                  isAutoComplete={true}
                  isLeadingIcon={false}
                  searchTerm={
                    typeof appsec.url === 'string'
                      ? appsec.url
                      : appsec.url && typeof appsec.url === 'object'
                        ? 'description' in appsec.url
                          ? appsec.url.description
                          : 'label' in appsec.url
                            ? appsec.url.label
                            : ''
                        : ''
                  }
                  onUpdate={async (query: string) => {
                    setError('')
                    try {
                      const {getItemsByTypeBySearchField} = await import('@/services/team/teamService')
                      const results = await getItemsByTypeBySearchField('AppSecFlow', {
                        search: query,
                        searchField: 'options.url',
                        size: 30,
                      })
                      const mapped = Array.isArray(results)
                        ? results
                            .map((item: any) => ({
                              id: item.id || item._id || item.value || item.label,
                              label: getRepoTitle(item),
                              fullObject: item,
                            }))
                            .filter(option => option.label && typeof option.label === 'string')
                        : []
                      setSuggestions(mapped)
                    } catch (err) {
                      setError(APPSEC_WIDGET.error)
                    }
                  }}
                  onSelectOption={(option: any) => updateAppsec(idx, option)}
                  onBlur={(e: any) => {
                    const value = e.target.value
                    if (isValidUrl(value)) {
                      updateAppsec(idx, {id: value, label: value})
                    }
                    handleBlur(idx, 'url')
                  }}
                />
                <Feedback
                  variant="info"
                  className={`${sharedStyles.infoLabel} ${sharedStyles.autocompleteLabelFont}`}
                  message={APPSEC_WIDGET.urlHelper}
                />
                {touched[idx]?.url && !appsec.url && <div className="mt-1 text-xs text-red-500">Required</div>}
                {touched[idx]?.url &&
                  appsec.url &&
                  duplicateInfo.duplicates.has(getNormalizedUrl(appsec.url)) &&
                  (duplicateInfo.firstIndex.get(getNormalizedUrl(appsec.url)) ?? idx) < idx && (
                    <div className="mt-1 text-xs text-red-500">
                      This repository is already configured in this Teams dashboard
                    </div>
                  )}
              </div>
              {error && (
                <div className="error" style={{color: 'red', marginTop: 4}}>
                  {error}
                </div>
              )}
            </div>
          </form>
        </CardWrapper>
      ))}
      <div className={sharedStyles.addPipelineButton}>
        <Button
          type="button"
          iconTrailing="add"
          size="medium"
          style={{marginTop: 8}}
          text="Add Repo"
          variant="tertiary"
          onClick={addAppsec}
        />
      </div>
    </>
  )
}

export default AppSecWidget
